/**
 * Funções puras compartilhadas pelos emitters de telemetria.
 *
 * Sem DI e sem Angular de propósito: são chamadas tanto de dentro de serviços
 * (GeocodeTraceService, OrderFlowTraceService) quanto de statics legados, e
 * precisam ser testáveis sem TestBed.
 *
 * Extraídas do GeocodeTraceService, que já rodava em produção — o mecanismo
 * estava certo, mas vivia colado ao escopo de endereço e não dava para reusar
 * no funil do pedido.
 */

/** Chaves de endereço sanitizadas no payload de telemetria (trim + null). */
const ADDRESS_KEYS = ['street', 'number', 'bairro', 'city', 'district', 'zone',
  'sug_street', 'sug_district', 'sug_city'];

/**
 * Timestamp no formato Hive `YYYY-MM-DD HH:MM:SS`.
 *
 * NÃO é cosmético: o ParquetSerDe do Firehose **rejeita** ISO 8601 com 'T' e
 * manda o evento inteiro para o bucket de erro, em silêncio.
 */
export function hiveTs(date: Date = new Date()): string {
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

/**
 * Distância aproximada em metros (Haversine) entre dois pontos lat/lng.
 * `null` se algum valor for não-finito ou se algum ponto for (0,0) — o app usa
 * (0,0) como "coordenada ausente", e medir a partir dele daria uma distância
 * finita e absurda em vez de um sinal de que o dado não existe.
 */
export function metersBetween(lat1: any, lng1: any, lat2: any, lng2: any): number | null {
  const a = Number(lat1), b = Number(lng1), c = Number(lat2), d = Number(lng2);
  if ([a, b, c, d].some(n => !isFinite(n)) || (a === 0 && b === 0) || (c === 0 && d === 0)) return null;
  const R = 6371000, toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(c - a), dLng = toRad(d - b);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a)) * Math.cos(toRad(c)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)));
}

/**
 * Sanitiza campos de endereço do payload (não muta o original): trim + colapsa
 * espaços internos; string vazia vira null. Conserta `GROUP BY` no Athena
 * ("Iririú " vs "Iririú") e o `city=''` indistinguível de null. Também trata os
 * objetos aninhados `before`/`after` do ClientGeoReverseGeocoded e o `on_screen` dos eventos de
 * saída da tela multissinal.
 */
export function normalizeAddressFields(data: any): any {
  if (!data || typeof data !== 'object') return data;
  const clean = (v: any) => {
    if (typeof v !== 'string') return v;
    const t = v.trim().replace(/\s+/g, ' ');
    return t === '' ? null : t;
  };
  const out: any = Array.isArray(data) ? [...data] : { ...data };
  for (const k of ADDRESS_KEYS) {
    if (k in out) out[k] = clean(out[k]);
  }
  for (const nested of ['before', 'after', 'on_screen']) {
    if (out[nested] && typeof out[nested] === 'object') {
      const o: any = { ...out[nested] };
      for (const kk of Object.keys(o)) o[kk] = clean(o[kk]);
      out[nested] = o;
    }
  }
  if ('city' in out) out.city_empty = out.city == null;
  return out;
}

/**
 * Mensagem de erro reduzida à FORMA, para poder sair do dispositivo.
 *
 * Mensagem de erro carrega dado de cliente com frequência (o endereço no erro de frete, o
 * nome do produto no de estoque) — é por isso que a allowlist da order-flow manda
 * `message` como `str(N)` (`order-flow-steps.ts`, shape `str`). Só que `str(64)` não
 * diagnostica: os dois erros mais frequentes do app somaram 7,7 mil ocorrências em
 * setembro/2026, em 1.516 lojas, e ninguém sabe o que são.
 *
 * Isto é o meio: mascara o que é DADO (e-mail, URL, token, sequência longa de dígitos) e
 * preserva o esqueleto da frase, que é o que agrupa ocorrências e identifica o defeito.
 * A ordem das substituições importa — e-mail e URL antes da regra de dígitos, senão
 * `joao123@x.com` viraria `joaoN@x.com` em vez de `<email>`.
 */
export function messageShape(msg: any, max = 120): string {
  const raw = typeof msg === 'string' ? msg : String((msg && msg.message) || msg || '');
  return raw
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '<email>')
    .replace(/https?:\/\/\S+/g, '<url>')
    // O lookahead duplo (tem dígito E tem letra) é o que separa token de palavra longa:
    // sem ele, qualquer sequência de 24+ caracteres sem espaço virava '<token>' e a
    // mensagem inteira sumia — inclusive uma frase legítima truncada.
    .replace(/\b(?=[\w-]*\d)(?=[\w-]*[a-zA-Z])[\w-]{24,}\b/g, '<token>')
    .replace(/\d{3,}/g, 'N')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

/** FNV-1a 32 bits. Determinístico e estável entre sessões/builds. */
export function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    // Multiplicação por 16777619 em 32 bits sem estourar para float.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/**
 * Bucket estável de amostragem (0..99) derivado do device.
 * Emitido dentro do `data` para dar para verificar uniformidade e alargar a
 * coorte retroativamente na query.
 */
export function sampleBucket(deviceId: string): number {
  if (!deviceId) return 0;
  return fnv1a(deviceId) % 100;
}

/**
 * O device está dentro da coorte de `pct`%?
 *
 * Determinística e **por device, nunca por evento**: amostrar por evento produz
 * funis com buracos (metade dos passos de uma mesma pessoa), e aí toda análise
 * de drop-off vira mentira.
 */
export function inSample(deviceId: string, pct: number): boolean {
  if (pct >= 100) return true;
  if (pct <= 0) return false;
  return sampleBucket(deviceId) < pct;
}
